// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { getDashboardStats, getRecentTransactions, getTransactionChart, getIssuerBreakdown, getRecentWithdrawals, getLatencyMetrics, getLatencyHistory, getSlaReport, getSecurityStatus, getSimulationConfig, toggleSimulation, runSimulationBatch, getMtlsStatus, getMtlsInstitutions, setMtlsEnforcement, revokeMtlsCert, reinstateMtlsCert } from "../lib/api";
import { 
  TrendingUp, 
  Receipt, 
  Building2, 
  DollarSign, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Store,
  ShoppingCart,
  ArrowRight,
  Activity,
  Zap,
  AlertTriangle,
  Shield,
  Timer,
  Lock,
  Play,
  Radio,
  ShieldCheck,
  Gauge,
  Loader2,
  Key,
  FileKey,
  ShieldAlert,
  Ban
} from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  LineChart,
  Line,
  ReferenceLine
} from "recharts";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatCard = ({ title, value, icon: Icon, trend, className = "", subtitle }) => (
  <Card className={`bg-card border-border card-hover ${className}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-bold font-mono">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="p-2 rounded-md bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1 text-xs">
          <TrendingUp className="h-3 w-3 text-green-400" />
          <span className="text-green-400">{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const statusStyles = {
    success: "bg-green-500/20 text-green-400 border-green-500/30",
    completed: "bg-green-500/20 text-green-400 border-green-500/30",
    failed: "bg-red-500/20 text-red-400 border-red-500/30",
    dispense_failed: "bg-red-500/20 text-red-400 border-red-500/30",
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    authorized: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    initiated: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    expired: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    cancelled: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${statusStyles[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
      {status}
    </span>
  );
};

const IssuerTypeBadge = ({ type }) => {
  if (type === 'retail_store') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
        <ShoppingCart className="h-3 w-3" /> Retail
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
      <Building2 className="h-3 w-3" /> Bank
    </span>
  );
};

const SlaStatusIndicator = ({ status }) => {
  const getStyles = () => {
    if (!status || status === "OK") return "bg-green-500/20 text-green-400 border-green-500/30";
    if (status === "WARNING") return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    return "bg-red-500/20 text-red-400 border-red-500/30";
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${getStyles()}`}>
      {status || 'OK'}
    </span>
  );
};

const RETAIL_COLOR = '#8b5cf6';
const BANK_COLOR = '#3b82f6';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentTx, setRecentTx] = useState([]);
  const [recentWithdrawals, setRecentWithdrawals] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [issuerBreakdown, setIssuerBreakdown] = useState([]);
  const [latencyMetrics, setLatencyMetrics] = useState(null);
  const [latencyHistory, setLatencyHistory] = useState([]);
  const [slaReport, setSlaReport] = useState(null);
  const [securityStatus, setSecurityStatus] = useState(null);
  const [simConfig, setSimConfig] = useState(null);
  const [simRunning, setSimRunning] = useState(false);
  const [simResults, setSimResults] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState("normal");
  const [simCount, setSimCount] = useState(10);
  const [mtlsStatus, setMtlsStatus] = useState(null);
  const [mtlsInstitutions, setMtlsInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, txRes, chartRes, issuerRes, withdrawalsRes, latencyRes, historyRes, slaRes, secRes, simRes, mtlsRes, mtlsInstRes] = await Promise.all([
        getDashboardStats(),
        getRecentTransactions(8),
        getTransactionChart(7),
        getIssuerBreakdown(),
        getRecentWithdrawals(10),
        getLatencyMetrics().catch(() => ({ data: null })),
        getLatencyHistory(50).catch(() => ({ data: { records: [] } })),
        getSlaReport(7).catch(() => ({ data: null })),
        getSecurityStatus().catch(() => ({ data: null })),
        getSimulationConfig().catch(() => ({ data: null })),
        getMtlsStatus().catch(() => ({ data: null })),
        getMtlsInstitutions().catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setRecentTx(txRes.data || []);
      setChartData(chartRes.data || []);
      setIssuerBreakdown(issuerRes.data || []);
      setRecentWithdrawals(withdrawalsRes.data || []);
      setLatencyMetrics(latencyRes.data);
      setLatencyHistory(historyRes.data?.records || []);
      setSlaReport(slaRes.data);
      setSecurityStatus(secRes.data);
      setSimConfig(simRes.data);
      setMtlsStatus(mtlsRes.data);
      setMtlsInstitutions(mtlsInstRes.data || []);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRunSimulation = async () => {
    setSimRunning(true);
    setSimResults(null);
    try {
      const res = await runSimulationBatch(simCount, selectedScenario);
      setSimResults(res.data);
      await fetchData();
    } catch (error) {
      console.error("Simulation failed:", error);
    } finally {
      setSimRunning(false);
    }
  };

  const handleToggleSimulation = async (enabled) => {
    try {
      await toggleSimulation(enabled, selectedScenario);
      const res = await getSimulationConfig();
      setSimConfig(res.data);
    } catch (error) {
      console.error("Toggle failed:", error);
    }
  };

  const handleMtlsEnforcement = async (mode) => {
    try {
      await setMtlsEnforcement(mode);
      const res = await getMtlsStatus();
      setMtlsStatus(res.data);
    } catch (error) {
      console.error("mTLS mode change failed:", error);
    }
  };

  const handleRevokeCert = async (dn) => {
    try {
      await revokeMtlsCert(dn);
      const [statusRes, instRes] = await Promise.all([getMtlsStatus(), getMtlsInstitutions()]);
      setMtlsStatus(statusRes.data);
      setMtlsInstitutions(instRes.data || []);
    } catch (error) {
      console.error("Revoke failed:", error);
    }
  };

  const handleReinstateCert = async (dn) => {
    try {
      await reinstateMtlsCert(dn);
      const [statusRes, instRes] = await Promise.all([getMtlsStatus(), getMtlsInstitutions()]);
      setMtlsStatus(statusRes.data);
      setMtlsInstitutions(instRes.data || []);
    } catch (error) {
      console.error("Reinstate failed:", error);
    }
  };

  const pieData = stats ? [
    { name: 'Retail Stores', value: stats.retail_store_transactions || 0, color: RETAIL_COLOR },
    { name: 'Banks', value: stats.bank_transactions || 0, color: BANK_COLOR }
  ].filter(d => d.value > 0) : [];

  const topIssuers = issuerBreakdown.slice(0, 6).map((item) => ({
    ...item,
    fill: item.issuer_type === 'retail_store' ? RETAIL_COLOR : BANK_COLOR
  }));

  const latencyChartData = [...latencyHistory].reverse().map((r, i) => ({
    index: i + 1,
    latency: r.total_latency_ms,
    sla_status: r.sla_status,
    timestamp: r.timestamp ? new Date(r.timestamp).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : ''
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="bg-card border-border">
              <CardContent className="p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-secondary rounded w-24" />
                  <div className="h-8 bg-secondary rounded w-32" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const metrics = latencyMetrics?.metrics;
  const slaConfig = latencyMetrics?.sla_config;
  const controls = securityStatus?.controls_active;
  const secEvents = securityStatus?.events_24h;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Transactions" value={stats?.total_transactions || 0} icon={Receipt} />
        <StatCard title="Total Volume" value={formatCurrency(stats?.total_volume)} icon={TrendingUp} />
        <StatCard title="Total Fees" value={formatCurrency(stats?.total_fees)} icon={DollarSign} />
        <StatCard title="Active Partners" value={(stats?.active_banks || 0) + (stats?.active_retail_stores || 0)} icon={Building2} subtitle={`${stats?.active_banks || 0} Banks, ${stats?.active_retail_stores || 0} Retail`} />
      </div>

      {/* Security Controls Panel */}
      {securityStatus && (
        <>
          <div className="flex items-center gap-2 pt-2">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            <h2 className="font-heading font-semibold text-base text-foreground">Security Controls</h2>
            <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold border ${
              securityStatus.security_score === 'HARDENED' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
            }`}>
              <Shield className="h-3 w-3" />
              {securityStatus.security_score}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="security-panel">
            {/* Active Controls */}
            <Card className="bg-card border-border" data-testid="security-controls-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-sm text-muted-foreground">Active Security Controls</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {controls && Object.entries(controls).map(([key, active]) => (
                    <div key={key} className={`flex items-center gap-2 p-2 rounded-md text-xs ${active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                      {active ? <CheckCircle2 className="h-3 w-3 flex-shrink-0" /> : <XCircle className="h-3 w-3 flex-shrink-0" />}
                      <span className="truncate">{key.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Security Events 24h */}
            <Card className="bg-card border-border" data-testid="security-events-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-sm text-muted-foreground">Security Events (24h)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-md bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-400" />
                      <span className="text-sm">Total Security Events</span>
                    </div>
                    <span className={`font-mono font-bold ${secEvents?.total_security_events > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {secEvents?.total_security_events || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-md bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-red-400" />
                      <span className="text-sm">Failed Logins</span>
                    </div>
                    <span className={`font-mono font-bold ${secEvents?.failed_logins > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {secEvents?.failed_logins || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-md bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <Radio className="h-4 w-4 text-orange-400" />
                      <span className="text-sm">Rate Limited Requests</span>
                    </div>
                    <span className="font-mono font-bold text-orange-400">{secEvents?.rate_limited_requests || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-md bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-400" />
                      <span className="text-sm">Duplicate Dispense Blocked</span>
                    </div>
                    <span className="font-mono font-bold text-muted-foreground">{secEvents?.duplicate_dispense_blocked || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* mTLS Certificate Management */}
      {mtlsStatus && (
        <>
          <div className="flex items-center gap-2 pt-2">
            <FileKey className="h-5 w-5 text-amber-400" />
            <h2 className="font-heading font-semibold text-base text-foreground">mTLS Certificate Management</h2>
            <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold border ${
              mtlsStatus.enforcement_mode === 'enforce' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
              mtlsStatus.enforcement_mode === 'log_only' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
              'bg-gray-500/20 text-gray-400 border-gray-500/30'
            }`}>
              <Key className="h-3 w-3" />
              {mtlsStatus.enforcement_mode.toUpperCase()}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="mtls-panel">
            {/* mTLS Overview */}
            <Card className="bg-card border-border" data-testid="mtls-overview-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-sm text-muted-foreground">Certificate Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-center">
                    <p className="text-2xl font-bold font-mono text-emerald-400">{mtlsStatus.active_certificates}</p>
                    <p className="text-xs text-muted-foreground">Active</p>
                  </div>
                  <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20 text-center">
                    <p className="text-2xl font-bold font-mono text-red-400">{mtlsStatus.revoked_certificates}</p>
                    <p className="text-xs text-muted-foreground">Revoked</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 rounded-md bg-secondary/30">
                  <span className="text-sm text-muted-foreground">CA Initialized</span>
                  {mtlsStatus.ca_initialized ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                </div>
                <div className="flex items-center justify-between p-2 rounded-md bg-secondary/30">
                  <span className="text-sm text-muted-foreground">Expiring Soon</span>
                  <span className={`font-mono font-bold ${mtlsStatus.expiring_soon?.length > 0 ? 'text-yellow-400' : 'text-emerald-400'}`}>{mtlsStatus.expiring_soon?.length || 0}</span>
                </div>
                {mtlsStatus.expiring_soon?.length > 0 && (
                  <div className="p-2 rounded-md bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-xs text-yellow-400 font-medium">Expiring certificates:</p>
                    {mtlsStatus.expiring_soon.map((c, i) => (
                      <p key={i} className="text-xs text-yellow-400">{c.institution} - {c.days_left} days left</p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Enforcement Mode */}
            <Card className="bg-card border-border" data-testid="mtls-enforcement-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-sm text-muted-foreground">Enforcement Mode</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">Controls how mTLS is enforced on switch-facing endpoints (/atm/authorize, /atm/dispense-notify)</p>
                <div className="space-y-2">
                  {['enforce', 'log_only', 'disabled'].map(mode => (
                    <button
                      key={mode}
                      onClick={() => handleMtlsEnforcement(mode)}
                      data-testid={`mtls-mode-${mode}`}
                      className={`w-full p-3 rounded-md text-left text-sm font-medium border transition-colors ${
                        mtlsStatus.enforcement_mode === mode
                          ? mode === 'enforce' ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : mode === 'log_only' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                            : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                          : 'bg-secondary/30 text-muted-foreground border-border hover:bg-secondary/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {mode === 'enforce' && <ShieldAlert className="h-4 w-4" />}
                        {mode === 'log_only' && <Shield className="h-4 w-4" />}
                        {mode === 'disabled' && <Ban className="h-4 w-4" />}
                        <span className="capitalize">{mode.replace('_', ' ')}</span>
                      </div>
                      <p className="text-xs mt-1 opacity-70">
                        {mode === 'enforce' && 'Reject all requests without valid client certificate'}
                        {mode === 'log_only' && 'Log invalid certs but allow (sandbox testing)'}
                        {mode === 'disabled' && 'No mTLS checking (development only)'}
                      </p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Dev Checklist */}
            <Card className="bg-card border-border" data-testid="mtls-checklist-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-sm text-muted-foreground">Security Checklist</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {mtlsStatus.developer_checklist && Object.entries(mtlsStatus.developer_checklist).map(([key, val]) => (
                    <div key={key} className={`flex items-center gap-2 p-2 rounded-md text-xs ${val ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                      {val ? <CheckCircle2 className="h-3 w-3 flex-shrink-0" /> : <XCircle className="h-3 w-3 flex-shrink-0" />}
                      <span className="truncate">{key.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Institution Certificates Table */}
          {mtlsInstitutions.length > 0 && (
            <Card className="bg-card border-border" data-testid="mtls-institutions-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Registered Institution Certificates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="mtls-institutions-table">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-left py-2 px-3 font-medium">Institution</th>
                        <th className="text-left py-2 px-3 font-medium">Role</th>
                        <th className="text-left py-2 px-3 font-medium">Serial</th>
                        <th className="text-left py-2 px-3 font-medium">Status</th>
                        <th className="text-left py-2 px-3 font-medium">Expires</th>
                        <th className="text-left py-2 px-3 font-medium">Days Left</th>
                        <th className="text-left py-2 px-3 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mtlsInstitutions.map((inst, idx) => (
                        <tr key={idx} className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
                          <td className="py-2 px-3 font-medium">{inst.institution_name}</td>
                          <td className="py-2 px-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${
                              inst.role === 'BANK' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                              inst.role === 'SWITCH' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' :
                              'bg-purple-500/20 text-purple-400 border-purple-500/30'
                            }`}>{inst.role}</span>
                          </td>
                          <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{inst.serial}</td>
                          <td className="py-2 px-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${
                              inst.status === 'active' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'
                            }`}>{inst.status}</span>
                          </td>
                          <td className="py-2 px-3 text-xs text-muted-foreground">
                            {inst.expires_at ? new Date(inst.expires_at).toLocaleDateString('en-ZA') : '-'}
                          </td>
                          <td className="py-2 px-3">
                            <span className={`font-mono text-sm ${
                              inst.days_until_expiry > 60 ? 'text-green-400' :
                              inst.days_until_expiry > 30 ? 'text-yellow-400' : 'text-red-400'
                            }`}>{inst.days_until_expiry ?? '-'}</span>
                          </td>
                          <td className="py-2 px-3">
                            {inst.status === 'active' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => handleRevokeCert(inst.dn)}
                                data-testid={`revoke-${inst.institution_name}`}
                              >
                                Revoke
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                onClick={() => handleReinstateCert(inst.dn)}
                                data-testid={`reinstate-${inst.institution_name}`}
                              >
                                Reinstate
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Latency & SLA Monitoring */}
      {latencyMetrics && (
        <>
          <div className="flex items-center gap-2 pt-2">
            <Activity className="h-5 w-5 text-cyan-400" />
            <h2 className="font-heading font-semibold text-base text-foreground">Latency Monitoring & SLA</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="latency-stats-grid">
            <Card className="bg-card border-border" data-testid="latency-avg-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-cyan-500/10">
                    <Zap className="h-6 w-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Latency</p>
                    <p className="text-2xl font-bold font-mono" data-testid="latency-avg-value">
                      {metrics?.avg_latency_ms?.toFixed(1) || '0'}ms
                    </p>
                    <p className="text-xs text-muted-foreground">Target: &lt;{slaConfig?.soft_sla_ms || 120}ms</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border" data-testid="latency-compliance-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-green-500/10">
                    <Shield className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">SLA Compliance</p>
                    <p className="text-2xl font-bold font-mono" data-testid="sla-compliance-value">
                      {metrics?.sla_compliance_rate?.toFixed(1) || '100'}%
                    </p>
                    <p className="text-xs text-muted-foreground">{metrics?.sla_breaches || 0} breaches</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border" data-testid="latency-sub150-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-emerald-500/10">
                    <Timer className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Under 150ms</p>
                    <p className="text-2xl font-bold font-mono">{metrics?.under_150ms_rate?.toFixed(1) || '0'}%</p>
                    <p className="text-xs text-muted-foreground">of {metrics?.total_authorizations || 0} authorizations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border" data-testid="latency-range-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${metrics?.sla_breaches > 0 ? 'bg-red-500/10' : 'bg-blue-500/10'}`}>
                    <Gauge className={`h-6 w-6 ${metrics?.sla_breaches > 0 ? 'text-red-400' : 'text-blue-400'}`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Latency Range</p>
                    <p className="text-lg font-bold font-mono">{metrics?.min_latency_ms?.toFixed(1) || '0'} - {metrics?.max_latency_ms?.toFixed(1) || '0'}ms</p>
                    <p className="text-xs text-muted-foreground">Max allowed: {slaConfig?.max_allowed_ms || 1500}ms</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Latency Chart + SLA Config */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="bg-card border-border lg:col-span-2" data-testid="latency-chart-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Authorization Latency Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  {latencyChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={latencyChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="timestamp" stroke="hsl(var(--muted-foreground))" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                        <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} tickFormatter={(v) => `${v}ms`} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(222.2 84% 4.9%)', border: '1px solid hsl(var(--border))', borderRadius: '6px' }} formatter={(value) => [`${value.toFixed(2)}ms`, 'Latency']} />
                        <ReferenceLine y={slaConfig?.soft_sla_ms || 120} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: `Soft SLA`, fill: '#f59e0b', fontSize: 11, position: 'right' }} />
                        <ReferenceLine y={slaConfig?.max_allowed_ms || 1500} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Max`, fill: '#ef4444', fontSize: 11, position: 'right' }} />
                        <Line type="monotone" dataKey="latency" stroke="#06b6d4" strokeWidth={2} dot={{ fill: '#06b6d4', r: 3 }} activeDot={{ r: 5, stroke: '#06b6d4', strokeWidth: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      No latency data yet - run simulations to generate metrics
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border" data-testid="sla-targets-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">SLA Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {[
                    { label: "Soft SLA", sub: "Warning threshold", val: slaConfig?.soft_sla_ms || 120, color: "text-yellow-400" },
                    { label: "Hard SLA", sub: "Fallback trigger", val: slaConfig?.hard_sla_ms || 180, color: "text-orange-400" },
                    { label: "Auth Timeout", sub: "Max auth time", val: slaConfig?.auth_timeout_ms || 150, color: "text-cyan-400" },
                    { label: "Max Allowed", sub: "Absolute ceiling", val: slaConfig?.max_allowed_ms || 1500, color: "text-red-400" },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between p-3 rounded-md bg-secondary/30">
                      <div>
                        <p className="text-sm font-medium">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.sub}</p>
                      </div>
                      <span className={`font-mono font-bold ${item.color}`}>{item.val}ms</span>
                    </div>
                  ))}
                </div>
                {slaReport && slaReport.total_transactions > 0 && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">7-Day SLA Report</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Under 150ms</span>
                        <span className="font-mono font-medium text-green-400">{slaReport.sla_compliance?.under_150ms_rate?.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Breach Rate</span>
                        <span className={`font-mono font-medium ${slaReport.sla_compliance?.breach_rate > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {slaReport.sla_compliance?.breach_rate?.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Latency History Table */}
          {latencyHistory.length > 0 && (
            <Card className="bg-card border-border" data-testid="latency-history-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Recent Authorization Latency</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="latency-history-table">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-left py-2 px-3 font-medium">Transaction</th>
                        <th className="text-left py-2 px-3 font-medium">Total</th>
                        <th className="text-left py-2 px-3 font-medium">DB Check</th>
                        <th className="text-left py-2 px-3 font-medium">Issuer Lookup</th>
                        <th className="text-left py-2 px-3 font-medium">Funds Lock</th>
                        <th className="text-left py-2 px-3 font-medium">DB Write</th>
                        <th className="text-left py-2 px-3 font-medium">SLA</th>
                        <th className="text-left py-2 px-3 font-medium">Sim</th>
                        <th className="text-left py-2 px-3 font-medium">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latencyHistory.slice(0, 10).map((record, idx) => (
                        <tr key={idx} className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
                          <td className="py-2 px-3">
                            <code className="text-xs font-mono text-muted-foreground">{record.transaction_id?.slice(0, 16)}...</code>
                          </td>
                          <td className="py-2 px-3">
                            <span className={`font-mono font-medium ${
                              record.total_latency_ms < (slaConfig?.soft_sla_ms || 120) ? 'text-green-400' :
                              record.total_latency_ms < (slaConfig?.max_allowed_ms || 1500) ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {record.total_latency_ms?.toFixed(2)}ms
                            </span>
                          </td>
                          <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{record.checkpoints?.idempotency_check?.toFixed(1) || '-'}ms</td>
                          <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{record.checkpoints?.issuer_lookup?.toFixed(1) || '-'}ms</td>
                          <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{record.checkpoints?.funds_locked?.toFixed(1) || '-'}ms</td>
                          <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{record.checkpoints?.db_write_complete?.toFixed(1) || '-'}ms</td>
                          <td className="py-2 px-3"><SlaStatusIndicator status={record.sla_status} /></td>
                          <td className="py-2 px-3">
                            {record.simulation_enabled ? (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                                {record.simulation_scenario || 'sim'}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">live</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs text-muted-foreground">{formatDate(record.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Network Latency Simulator */}
      <div className="flex items-center gap-2 pt-2">
        <Play className="h-5 w-5 text-purple-400" />
        <h2 className="font-heading font-semibold text-base text-foreground">Network Latency Simulator</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="simulation-panel">
        <Card className="bg-card border-border" data-testid="simulation-controls">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              Simulation Controls
              {simConfig?.enabled && <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">LIVE</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Scenario</label>
              <div className="grid grid-cols-2 gap-2">
                {['optimal', 'normal', 'degraded', 'worst_case'].map(s => (
                  <button
                    key={s}
                    onClick={() => setSelectedScenario(s)}
                    data-testid={`scenario-${s}`}
                    className={`p-2 rounded-md text-xs font-medium border transition-colors ${
                      selectedScenario === s
                        ? 'bg-primary/20 text-primary border-primary/30'
                        : 'bg-secondary/30 text-muted-foreground border-border hover:bg-secondary/50'
                    }`}
                  >
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Batch Size: {simCount}</label>
              <input
                type="range"
                min="1"
                max="50"
                value={simCount}
                onChange={e => setSimCount(Number(e.target.value))}
                className="w-full accent-primary"
                data-testid="sim-batch-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>1</span>
                <span>50</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleRunSimulation}
                disabled={simRunning}
                className="flex-1"
                data-testid="run-simulation-btn"
              >
                {simRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Run Batch ({simCount} txns)
                  </>
                )}
              </Button>
              <Button
                variant={simConfig?.enabled ? "destructive" : "outline"}
                onClick={() => handleToggleSimulation(!simConfig?.enabled)}
                data-testid="toggle-simulation-btn"
              >
                {simConfig?.enabled ? "Disable Live" : "Enable Live"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="simulation-results">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Simulation Results</CardTitle>
          </CardHeader>
          <CardContent>
            {simResults ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-md bg-cyan-500/10 border border-cyan-500/20">
                    <p className="text-xs text-muted-foreground">Avg Latency</p>
                    <p className="text-xl font-bold font-mono text-cyan-400">{simResults.avg_latency_ms}ms</p>
                  </div>
                  <div className="p-3 rounded-md bg-blue-500/10 border border-blue-500/20">
                    <p className="text-xs text-muted-foreground">Range</p>
                    <p className="text-xl font-bold font-mono text-blue-400">{simResults.min_latency_ms}-{simResults.max_latency_ms}ms</p>
                  </div>
                  <div className="p-3 rounded-md bg-green-500/10 border border-green-500/20">
                    <p className="text-xs text-muted-foreground">Batch Size</p>
                    <p className="text-xl font-bold font-mono text-green-400">{simResults.batch_size}</p>
                  </div>
                  <div className={`p-3 rounded-md border ${simResults.sla_breaches > 0 ? 'bg-red-500/10 border-red-500/20' : 'bg-emerald-500/10 border-emerald-500/20'}`}>
                    <p className="text-xs text-muted-foreground">SLA Breaches</p>
                    <p className={`text-xl font-bold font-mono ${simResults.sla_breaches > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{simResults.sla_breaches}</p>
                  </div>
                </div>
                <div className="max-h-[200px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-left py-1 px-2">Bank</th>
                        <th className="text-left py-1 px-2">Latency</th>
                        <th className="text-left py-1 px-2">SLA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simResults.results?.map((r, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1 px-2 font-mono">{r.bank}</td>
                          <td className={`py-1 px-2 font-mono ${r.latency_ms < 150 ? 'text-green-400' : r.latency_ms < 400 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {r.latency_ms.toFixed(1)}ms
                          </td>
                          <td className="py-1 px-2"><SlaStatusIndicator status={r.sla_status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
                <Play className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">Run a simulation to see results</p>
                <p className="text-xs mt-1">Choose scenario and batch size, then click Run</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Issuer Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-500/10"><Store className="h-6 w-6 text-purple-400" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Retail Store Tx</p>
                <p className="text-2xl font-bold font-mono">{stats?.retail_store_transactions || 0}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(stats?.retail_store_volume)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-500/10"><Building2 className="h-6 w-6 text-blue-400" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Bank Tx</p>
                <p className="text-2xl font-bold font-mono">{stats?.bank_transactions || 0}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(stats?.bank_volume)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-500/10"><CheckCircle2 className="h-6 w-6 text-green-400" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Successful</p>
                <p className="text-2xl font-bold font-mono">{stats?.successful_transactions || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-yellow-500/10"><Clock className="h-6 w-6 text-yellow-400" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Auth</p>
                <p className="text-2xl font-bold font-mono">{stats?.pending_authorizations || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Transaction Volume (7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} tickFormatter={(v) => v ? v.slice(5) : ''} />
                    <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} tickFormatter={(v) => `R${v}`} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(222.2 84% 4.9%)', border: '1px solid hsl(var(--border))', borderRadius: '6px' }} formatter={(value) => [formatCurrency(value), 'Volume']} />
                    <Area type="monotone" dataKey="volume" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorVolume)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">No data</div>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Transaction Split</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={5} dataKey="value">
                      {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(222.2 84% 4.9%)', border: '1px solid hsl(var(--border))', borderRadius: '6px' }} />
                    <Legend verticalAlign="bottom" formatter={(value) => (<span style={{ color: 'hsl(var(--foreground))' }}>{value}</span>)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">No data</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Issuers */}
      {topIssuers.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="font-heading text-lg">Top Issuers by Transaction Count</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topIssuers} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                  <YAxis type="category" dataKey="issuer_name" stroke="hsl(var(--muted-foreground))" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} width={100} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(222.2 84% 4.9%)', border: '1px solid hsl(var(--border))', borderRadius: '6px' }} formatter={(value, name, props) => [`${value} (${formatCurrency(props.payload.total_volume)})`, props.payload.issuer_type === 'retail_store' ? 'Retail' : 'Bank']} />
                  <Bar dataKey="transaction_count" radius={[0, 4, 4, 0]}>
                    {topIssuers.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="font-heading text-lg">Recent Completed</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentTx.length > 0 ? recentTx.slice(0, 6).map((tx) => (
                <div key={tx.id} className="flex items-center justify-between p-3 rounded-md bg-secondary/30 hover:bg-secondary/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-muted-foreground">{(tx.voucher_reference || tx.id || 'N/A').slice(0, 10)}...</code>
                      <StatusBadge status={tx.status} />
                    </div>
                    <div className="flex items-center gap-1 text-sm mt-1">
                      <span className="text-muted-foreground">{tx.issuer_bank_code}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">{tx.acquirer_bank_code}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium">{formatCurrency(tx.dispensed_amount)}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(tx.timestamp)}</p>
                  </div>
                </div>
              )) : <div className="text-center py-8 text-muted-foreground">No completed transactions</div>}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="font-heading text-lg">Recent Withdrawals</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentWithdrawals.length > 0 ? recentWithdrawals.slice(0, 6).map((w) => (
                <div key={w.id} className="flex items-center justify-between p-3 rounded-md bg-secondary/30 hover:bg-secondary/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <IssuerTypeBadge type={w.issuer_type_label} />
                      <StatusBadge status={w.status} />
                    </div>
                    <div className="flex items-center gap-1 text-sm mt-1">
                      <span className="font-medium text-foreground">{w.issuer_name || w.issuer_id}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">{w.acquirer_id}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium">{formatCurrency(w.amount_requested)}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(w.created_at)}</p>
                  </div>
                </div>
              )) : <div className="text-center py-8 text-muted-foreground">No recent withdrawals</div>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
